import { Button, Stack, Typography } from '@mui/material';
import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import type { FormikHelpers } from 'formik';
import { useFormik } from 'formik';

import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { MangopayConnectFormStep1 } from './MangopayConnectFormStep1';
import { MangopayConnectFormStep2 } from './MangopayConnectFormStep2';
import {
  MangopayConnectFormStep,
  useConnectMangopayServiceProviderAccountValidationSchema,
} from '@pflegenavi/shared/frontend';
import type {
  ConnectMangopayLegalRepresentative,
  ConnectMangopayServiceProvider,
} from '@pflegenavi/service-provider-frontend/api';
import { LegalPersonType } from '@pflegenavi/service-provider-frontend/api';
import { LoadingPromiseButtonAdvanced } from '@pflegenavi/web-components';

export interface IMangopayConnectFormValues {
  service_provider: ConnectMangopayServiceProvider;
  legal_representative: ConnectMangopayLegalRepresentative;
}

interface ConnectFormProps {
  companyDetails: {
    name: string;
    address: string;
    city: string;
    zipCode: string;
    phone: string;
    countryId: string;
  };
  userDetails: {
    firstName: string;
    lastName: string;
    email: string;
  };
  countryOptions: Array<{
    label: string;
    value: string;
  }>;
  loading: boolean;
  accountAffiliationName?: string;
  handleConnectAccount: (
    service_provider: ConnectMangopayServiceProvider,
    legal_representative: ConnectMangopayLegalRepresentative
  ) => Promise<void>;
  mangopayUserAccountConnected: boolean;
}

export const MangopayConnectForm: FC<ConnectFormProps> = ({
  companyDetails,
  userDetails,
  countryOptions,
  loading,
  accountAffiliationName,
  handleConnectAccount,
  mangopayUserAccountConnected,
}) => {
  const { t } = useTranslation();

  const [step, setStep] = useState(MangopayConnectFormStep.Step1);

  const validationSchema = Yup.object(
    useConnectMangopayServiceProviderAccountValidationSchema(step)
  ).required();

  const handleSubmit = useCallback(
    async (
      values: IMangopayConnectFormValues,
      helpers: FormikHelpers<IMangopayConnectFormValues>
    ) => {
      if (step === MangopayConnectFormStep.Step1) {
        setStep(MangopayConnectFormStep.Step2);
      } else if (step === MangopayConnectFormStep.Step2) {
        await handleConnectAccount(
          values.service_provider,
          values.legal_representative
        );
        // After successful account creation, the page will reload with the recipient form
      }
    },
    [handleConnectAccount, step]
  );

  const initialValues: IMangopayConnectFormValues = useMemo(() => {
    return {
      service_provider: {
        name: companyDetails.name,
        legal_person_type: LegalPersonType.Business,
        email: userDetails.email,
        company_number: companyDetails.phone,
        headquarters_address: {
          address: companyDetails.address,
          city: companyDetails.city,
          zip_code: companyDetails.zipCode,
          country_id: companyDetails.countryId,
        },
        phone: companyDetails.phone,
      },
      legal_representative: {
        first_name: userDetails.firstName,
        last_name: userDetails.lastName,
        birth_date: undefined,
        nationality_id: '',
        country_of_residence_id: '',
      },
    };
  }, [
    companyDetails.address,
    companyDetails.city,
    companyDetails.countryId,
    companyDetails.name,
    companyDetails.phone,
    companyDetails.zipCode,
    userDetails.firstName,
    userDetails.email,
    userDetails.lastName,
  ]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: false, // To avoid the form "blinking" when the initialValues change e.g. during submit
    onSubmit: handleSubmit,
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true, // To avoid the form being validate again during submission when the first request is finished
  });

  const disabled = useMemo(() => {
    return formik.isSubmitting || loading;
  }, [formik.isSubmitting, loading]);

  const handleAdvanceStep1 = useCallback(async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      setStep(MangopayConnectFormStep.Step2);
    } else {
      // Mark all fields as touched to show validation errors
      formik.setTouched({
        service_provider: {
          name: true,
          company_number: true,
          legal_person_type: true,
          email: true,
          phone: true,
          headquarters_address: {
            address: true,
            zip_code: true,
            city: true,
            country_id: true,
          },
        },
      });
    }
  }, [formik]);

  const handleBack = useCallback(() => {
    if (step === MangopayConnectFormStep.Step2) {
      setStep(MangopayConnectFormStep.Step1);
      return;
    }
  }, [step]);

  const backButton = useMemo(() => {
    if (step === MangopayConnectFormStep.Step2) {
      return (
        <Button
          variant="outlined"
          size="large"
          onClick={handleBack}
          disabled={formik.isSubmitting}
          fullWidth
        >
          {t('service-provider-app.connect-account.back')}
        </Button>
      );
    }
    return null;
  }, [formik.isSubmitting, handleBack, step, t]);

  const formBody = useMemo(() => {
    const forms = {
      [MangopayConnectFormStep.Step1]: (
        <MangopayConnectFormStep1
          formik={formik}
          countryOptions={countryOptions}
          disabled={disabled}
        />
      ),
      [MangopayConnectFormStep.Step2]: (
        <MangopayConnectFormStep2
          formik={formik}
          countryOptions={countryOptions}
          disabled={disabled}
        />
      ),
    };

    return (
      <Stack gap={2}>
        {forms[step]}
        <Stack direction="row" justifyContent="center" gap={1}>
          {backButton}
          <LoadingPromiseButtonAdvanced
            variant="contained"
            size="large"
            disabled={formik.dirty ? !formik.isValid || disabled : true}
            onClick={
              step === MangopayConnectFormStep.Step1
                ? handleAdvanceStep1
                : formik.submitForm
            }
            fullWidth
          >
            {t('service-provider-app.connect-account.continue')}
          </LoadingPromiseButtonAdvanced>
        </Stack>
      </Stack>
    );
  }, [
    backButton,
    countryOptions,
    disabled,
    formik,
    handleAdvanceStep1,
    step,
    t,
  ]);

  if (!accountAffiliationName) {
    return (
      <Stack alignItems="center" pt={4}>
        <Stack maxWidth={700} gap={3}>
          <Stack gap={0.5}>
            <Typography variant="h5" color="primary.main">
              {t('service-provider-app.connect-account.invitation-invalid')}
            </Typography>
            <Typography variant="subtitle2" color="grey.500">
              {t(
                'service-provider-app.connect-account.invitation-invalid-description'
              )}
            </Typography>
          </Stack>
          {formBody}
          <Stack direction="row" justifyContent="center" gap={0.5}>
            <Typography variant="subtitle2" color="grey.500">
              {t('service-provider-app.connect-account.account-affiliation')}:
            </Typography>
            <Typography
              variant="subtitle2"
              color="primary.main"
              fontWeight={700}
            >
              {t('service-provider-app.connect-account.no-affiliation')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" pt={4}>
      <Stack maxWidth={700} gap={3}>
        <Stack gap={0.5}>
          <Typography variant="h5" color="primary.main">
            {t('service-provider-app.connect-account.you-are-invited')}
          </Typography>
          <Typography variant="subtitle2" color="grey.500">
            {t(
              'service-provider-app.connect-account.you-are-invited-description'
            )}
          </Typography>
        </Stack>
        {formBody}
        <Stack direction="row" justifyContent="center" gap={0.5}>
          <Typography variant="subtitle2" color="grey.500">
            {t('service-provider-app.connect-account.account-affiliation')}:
          </Typography>
          <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
            {accountAffiliationName}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
